import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "sdf_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const findIdx = (productId, variantId) =>
    items.findIndex((i) => i.product_id === productId && i.variant_id === variantId);

  const addItem = (item) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.product_id === item.product_id && i.variant_id === item.variant_id
      );
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: item.quantity };
        return copy;
      }
      return [...prev, item];
    });
  };

  const updateQuantity = (productId, variantId, qty) => {
    setItems((prev) => {
      const idx = findIdx(productId, variantId);
      if (idx < 0) return prev;
      const copy = [...prev];
      if (qty <= 0) copy.splice(idx, 1);
      else copy[idx] = { ...copy[idx], quantity: qty };
      return copy;
    });
  };

  const removeItem = (productId, variantId) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === productId && i.variant_id === variantId)));
  };

  const clear = () => setItems([]);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0),
    [items]
  );

  const itemCount = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const getQtyFor = (productId, variantId) => {
    const it = items.find((i) => i.product_id === productId && i.variant_id === variantId);
    return it ? it.quantity : 0;
  };

  const getProductQty = (productId) => {
    return items.filter((i) => i.product_id === productId).reduce((s, i) => s + i.quantity, 0);
  };

  return (
    <CartCtx.Provider
      value={{ items, addItem, updateQuantity, removeItem, clear, total, itemCount, getQtyFor, getProductQty }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
